import { projectMenu, resourceMenuId } from "@/features/demo_menu/model/menuPolicy";
import { SAFETY_REPORTS_DEMO_MENU_ID, SAFETY_REPORTS_DEMO_ROUTE } from '@/features/safety_reports/shared/constants/safetyReportsRoutes';
import { getDemoMenuConfiguration } from "@/features/demo_menu/services/demoMenuApi";
import { logout, loginSuccess } from "./auth/authSlice";
import { AppLayoutConfig } from "@/interface/app-layout-config.interface";
import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import type { ResourceWithChildren } from "@/services/MenuService";

// Import types only to avoid circular dependency
import type { Permission } from "@/lib/api/entitlements";

// Menu configuration interface
export interface MenuItem {
  id?: string;
  icon: string;
  label: string;
  route: string;
  requiredResource?: string;
  requiredAction?: string;
  items?: MenuItem[];
  disabled?: boolean;
}

interface MenuConfig {
  sidenav: Array<{
    name: string;
    items: Array<{
      name: string;
      id?: string;
      disabled?: boolean;
      route?: string;
      icon?: string;
      items: Array<MenuItem>;
    }>;
  }>;
}

// Helper to map API response to UI MenuConfig.
// Exported so unit tests can exercise the REMOVED_ROUTES / LOCKED_ROUTES
// rules without driving the full thunk + Redux store.
export const mapApiMenuToConfig = (apiModules: ResourceWithChildren[]): MenuConfig => {
  // 1. Sort modules by display_order
  const sortedModules = [...apiModules].sort((a, b) =>
    (a.display_order || 999) - (b.display_order || 999)
  );

  // 2. Define Section Grouping
  // Safety DB is intentionally excluded — its items are surfaced via the
  // hardcoded Case Processing and Compliance modules injected below.
  const sections: Record<string, string[]> = {
    "MAIN": ["Dashboard"],
    "SYSTEM": ["Configuration"]
  };

  // 3. Helper to get icon
  const getIcon = (name: string, apiIcon?: string | null): string => {
    if (apiIcon) return apiIcon; // Use API icon if available

    // Fallback mappings
    const lower = name.toLowerCase();
    if (lower.includes('dashboard')) return 'Home';
    if (lower.includes('case')) return 'FileText';
    if (lower.includes('report')) return 'BarChart';
    if (lower.includes('configuration') || lower.includes('admin')) return 'Settings';
    if (lower.includes('overview') || lower.includes('layout')) return 'Layout';
    if (lower.includes('medical')) return 'MessageCircle';
    if (lower.includes('literature')) return 'BookOpen'; // Better default for lit review
    if (lower.includes('signal')) return 'Activity';
    if (lower.includes('safety')) return 'Shield';

    return 'Circle'; // Default
  };

  // Resources surfaced via other menu sections; suppress here to avoid duplicate entries.
  // Case Intake is rendered as "Manual Intake" under Assignment Center; the backend
  // resource still exists for granular permission checks.
  const HIDDEN_RESOURCE_NAMES = new Set<string>(["Case Intake", "Permissions & Sections", "Permissions", "Entitlements"]);

  // Routes the UI hides regardless of backend Resource presence.
  const REMOVED_ROUTES = new Set<string>([
    "/dashboard", // Analytics — removed from UI
  ]);

  // Routes the UI renders as locked (visible, non-clickable, lock icon).
  const LOCKED_ROUTES = new Set<string>([
    "/ri-dashboard", // Regulatory Intelligence — not yet available
    "/reports",      // Safety Reports — superseded by Aggregate Reports
    "/gateway",      // ICSR Transmission — superseded by Transmission
  ]);

  // Map API resource names to desired display labels.
  const LABEL_OVERRIDES: Record<string, string> = {
    "Dashboard Operations":     "Operations",
    "Conversational Dashboard": "DialogueAI",
    "Conversation Dashboard":   "DialogueAI",
    "RI Dashboard":             "Regulatory Intelligence",
    "Configurations":           "System Settings",
    "ICSR Transmission":        "Transmission",
    "Safety Reports":           "Aggregate Reports",
  };

  // Helper to recursively map resources to items
  const mapResourcesToItems = (resources: ResourceWithChildren[]): MenuItem[] => {
    return resources
      .filter(resource => !HIDDEN_RESOURCE_NAMES.has(resource.name))
      .filter(resource => !(resource.route && REMOVED_ROUTES.has(resource.route)))
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(resource => {
        const route = resource.route || '#';
        const locked = resource.route ? LOCKED_ROUTES.has(resource.route) : false;
        return {
          id: resourceMenuId(resource.id),
          icon: getIcon(resource.name, resource.icon),
          label: LABEL_OVERRIDES[resource.name] ?? resource.name,
          route,
          ...(locked ? { disabled: true } : {}),
          items: resource.child_resources && resource.child_resources.length > 0
            ? mapResourcesToItems(resource.child_resources)
            : undefined
        };
      });
  };

  // 4. Build Sections
  const builtSections: Array<{ name: string; items: any[] }> = [];

  // Iterate over defined sections to maintain order: MAIN -> SAFETY -> SYSTEM
  Object.entries(sections).forEach(([sectionName, moduleNames]) => {
    const sectionItems: any[] = [];

    moduleNames.forEach(moduleName => {
      const module = sortedModules.find(m => m.name === moduleName);
      if (module) {
        // Map children recursively
        // CHANGED: Do NOT create artificial child if no children. Just return empty array.
        const moduleItems = module.child_resources && module.child_resources.length > 0
          ? mapResourcesToItems(module.child_resources)
          : [];

        if (moduleItems.length > 0 || module.route) {
          sectionItems.push({
            id: resourceMenuId(module.id),
            name: module.name,
            route: module.route, // Pass route to module level
            items: moduleItems
          });
        }
      }
    });

    if (sectionItems.length > 0) {
      builtSections.push({
        name: sectionName,
        items: sectionItems
      });
    }
  });

  // 5. Handle "Other" modules (anything not in the predefined groups)
  // Modules listed here are fully suppressed — their items are surfaced
  // via the hardcoded Case Processing / Compliance modules instead.
  const SUPPRESSED_MODULES = new Set<string>(["Safety DB"]);

  const groupedModuleNames = Object.values(sections).flat();
  const otherModules = sortedModules.filter(
    m => !groupedModuleNames.includes(m.name) && !SUPPRESSED_MODULES.has(m.name)
  );

  if (otherModules.length > 0) {
    const otherItems = otherModules.map(module => ({
      id: resourceMenuId(module.id),
      name: module.name,
      route: module.route,
      items: module.child_resources && module.child_resources.length > 0
        ? mapResourcesToItems(module.child_resources)
        : []
    }));

    // Check if "OTHERS" exists or append
    builtSections.push({
      name: "OTHERS",
      items: otherItems // This is effectively an array of groups
    });
  }

  injectAiSubmenuUnderConfiguration(builtSections);

  return {
    sidenav: builtSections
  };
};

/**
 * Append a static "AI" submenu (currently just "Case Schema") under the
 * Configuration module. This is a client-side-only menu entry — no
 * backend Resource row is required, and the page it links to is
 * read-only (the user downloads edited JSON locally).
 */
function injectAiSubmenuUnderConfiguration(
  sections: Array<{ name: string; items: any[] }>
): void {
  const AI_SUBMENU = {
    id: '/Configuration/AI',
    icon: 'Cpu',
    label: 'AI',
    route: '#',
    items: [
      {
        id: '/Configuration/AI/CaseSchema',
        icon: 'FileJson',
        label: 'Case Schema',
        route: '/configuration/ai/case-schema',
      },
    ],
  };

  for (const section of sections) {
    const config = section.items.find((m: any) => m.name === 'Configuration');
    if (!config) continue;
    if (!Array.isArray(config.items)) config.items = [];
    const alreadyPresent = config.items.some((m: any) => m.label === 'AI');
    if (!alreadyPresent) {
      config.items.push(AI_SUBMENU);
    }
    return;
  }
}

function assembleMenu(config: MenuConfig): MenuConfig {
        const caseProcessingModule = {
          id: "/CaseProcessing", name: "Case Processing",
          items: [
            { icon: "Upload", id: "/CaseProcessing/FileUpload", label: "File Upload", route: "/file-upload" },
            { icon: "LayoutDashboard", id: "/CaseProcessing/IntakeAssignment", label: "Intake & Assignment", route: "/assignment-center" },
            { icon: "List", id: "/CaseProcessing/WorkQueue", label: "Work Queue", route: "/assignment-center/work-queue" },
            { icon: "ClipboardList", id: "/CaseProcessing/IntakeTriage", label: "Intake Triage", route: "/intake-triage" },
            { icon: "FileText", id: "/CaseProcessing/CaseSearch", label: "Case Search", route: "/list" },
            { icon: "List", id: "/CaseProcessing/LineListing", label: "Line Listing", route: "/line-listing" },
            { icon: "Search", id: "/CaseProcessing/QueryManagement", label: "Query Management", route: "/query-manager" },
            { icon: "Send", id: "/CaseProcessing/Transmissions", label: "Transmissions", route: "", disabled: true },
            { icon: "BarChart", id: SAFETY_REPORTS_DEMO_MENU_ID, label: "Safety Reports", route: SAFETY_REPORTS_DEMO_ROUTE },
          ]
        };

        const complianceModule = {
          id: "/Compliance", name: "Compliance",
          items: [
            { icon: "List", id: "/Compliance/AuditTrail", label: "Audit Trail", route: "/audit-trail" },
            { icon: "Archive", id: "/Compliance/CaseArchive", label: "Case Archive", route: "", disabled: true },
          ]
        };

        // E2B(R3) export module — sibling to Case Processing inside
        // SAFETY & COMPLIANCE. The leaf "All Cases" route renders the
        // case list with per-row XML / PDF download actions.
        const e2bR3Module = {
          id: "/E2BRelease3", name: "E2B Release 3",
          items: [
            { icon: "FileCode", id: "/E2BRelease3/AllCases", label: "All Cases", route: "/e2b-r3" },
          ],
        };

        // Insert Case Processing → E2B Release 3 → Compliance after
        // Dashboard (MAIN) and before Configuration (SYSTEM) to
        // produce the sidebar order:
        //   Dashboard → Case Processing → E2B Release 3 → Compliance → Configuration
        const sidenav = config.sidenav;
        const mainIndex = sidenav.findIndex((s: any) => s.name === "MAIN");
        const insertAt = mainIndex >= 0 ? mainIndex + 1 : sidenav.length;
        sidenav.splice(insertAt, 0, {
          name: "SAFETY & COMPLIANCE",
          items: [caseProcessingModule, e2bR3Module, complianceModule]
        });

        // Pin "System Settings" as the first item in the Configuration module
        const systemSection = sidenav.find((s: any) => s.name === "SYSTEM");
        const configModule = systemSection?.items?.find((m: any) => m.name === "Configuration");
        if (configModule?.items) {
          const sysSettingsIndex = configModule.items.findIndex((i: any) => i.label === "System Settings");
          if (sysSettingsIndex > 0) {
            const [sysSettings] = configModule.items.splice(sysSettingsIndex, 1);
            configModule.items.unshift(sysSettings);
          }
        }


return { sidenav };
}

// Async thunk for fetching dynamic user menu
export const fetchDynamicMenu = createAsyncThunk(
  'layoutConfig/fetchDynamicMenu',
  async (_, { rejectWithValue }) => {
    try {
      const { default: MenuService } = await import("@/services/MenuService");
      const [menuResponse, demo] = await Promise.all([MenuService.getMyMenu(), getDemoMenuConfiguration()]);
      if (!demo.revision) throw new Error(demo.error || 'Demo menu configuration is unavailable');
      const config = assembleMenu(mapApiMenuToConfig(menuResponse.modules));
      return { ...projectMenu(config.sidenav, demo.overrides ?? {}), warning: demo.error ?? null };
    } catch (error: any) {
      console.error('Error fetching dynamic menu:', error);
      return rejectWithValue(error.message || 'Failed to fetch menu');
    }
  },
  {
    condition: (_, { getState }) => {
      const state: any = getState();
      const loading = state.layoutConfig?.loading;
      const loaded = state.layoutConfig?.permissionsLoaded; // Repurpose this flag or just check if sidenav has items?
      // Utilizing permissionsLoaded for now as it signifies data readiness
      return !loading && !loaded;
    },
  }
);

/* 
  Legacy/Static references kept but unused or for fallback if needed.
  (generateMenuFromRole, etc. can be removed if we fully switch)
*/

// Deprecated: Generate menu configuration based on user role
// const generateMenuFromRole = (role: string): MenuConfig => {
//   const baseMenu = {
//     sidenav: [
//       {
//         name: "MAIN",
//         items: [
//           {
//             name: "Dashboard",
//             items: [
//               { icon: "Home", label: "Operations", route: "/operations", requiredResource: "Dashboard Operations" },
//               { icon: "BarChart", label: "My Dashboard", route: "/dashboard", requiredResource: "Dashboard Analytics" },
//               { icon: "", label: "DialogueAI", route: "/conversation-dashboard", requiredResource: "Conversation Dashboard" },
//               { icon: "", label: "Regulatory Intelligence", route: "/ri-dashboard", requiredResource: "RI Dashboard" },
//             ]
//           }
//         ]
//       },
//       {
//         name: "SAFETY & COMPLIANCE",
//         items: [
//           {
//             name: "Safety DB",
//             items: [
//               { icon: "FileText", label: "Case Search", route: "/list", requiredResource: "Case Search" },
//               { icon: "ClipboardList", label: "Case Intake", route: "/case-intake", requiredResource: "Case Intake" },
//               { icon: "BarChart", label: "Reports", route: "/reports", requiredResource: "Safety Reports" },
//               { icon: "List", label: "Audit Trail", route: "/audit-trail", requiredResource: "Audit Trail" },
//               { icon: "List", label: "Line Listing", route: "/line-listing", requiredResource: "Line Listing" },
//               { icon: "Gateway", label: "ICSR Transmission", route: "/gateway", requiredResource: "Case Management" }
//             ]
//           },
//           {
//             name: "Medical Inquiry",
//             items: [
//               { icon: "MessageCircle", label: "Medical Inquiry", route: "/medical-inquiry", requiredResource: "Medical Inquiry" }
//             ]
//           }
//         ]
//       }
//     ]
//   };

//   // Add admin-only sections
//   if (role.toLowerCase() === 'admin') {
//     baseMenu.sidenav[1].items.push(
//       {
//         name: "Literature Review",
//         items: [
//           { icon: "Inbox", label: "Literature Inbox", route: "/literature-inbox", requiredResource: "Literature Inbox" },
//           { icon: "BarChart", label: "Knowledge Graph", route: "/knowledge-graph", requiredResource: "Knowledge Graph" },
//           { icon: "ClipboardCheck", label: "Article Triage", route: "/article-triage", requiredResource: "Article Triage" },
//           { icon: "AlertTriangle", label: "Extracted AEs", route: "/extracted-aes", requiredResource: "Extracted AEs" },
//           { icon: "Calendar", label: "Due Reports", route: "/due-reports", requiredResource: "Due Reports" },
//           { icon: "FileText", label: "Executive Summaries", route: "/executive-summaries", requiredResource: "Executive Summaries" },
//           { icon: "Search", label: "Search & Download Logs", route: "/search-logs", requiredResource: "Search Download Logs" }
//         ]
//       },
//       {
//         name: "Signal Detection",
//         items: [
//           { icon: "PieChart", label: "Disproportionality Analysis", route: "/disproportionality-analysis", requiredResource: "Disproportionality Analysis" },
//           { icon: "Activity", label: "Signal Tracker", route: "/signal-tracker", requiredResource: "Signal Tracker" },
//           { icon: "BarChart3", label: "Signal Review", route: "/signal-review", requiredResource: "Signal Review" },
//           { icon: "BarChart3", label: "Signal Forum", route: "/signal-forum", requiredResource: "Signal Forum" },
//           { icon: "FileText", label: "Aggregate Reports (PSUR/PBRER)", route: "/aggregate-reports", requiredResource: "Aggregate Reports PSUR" },
//           { icon: "TrendingUp", label: "Trend Detection", route: "/trend-detection", requiredResource: "Trend Detection" },
//           { icon: "CheckCircle", label: "Signal Validation", route: "/signal-validation", requiredResource: "Signal Validation" },
//           { icon: "History", label: "Escalation History", route: "/escalation-history", requiredResource: "Escalation History" }
//         ]
//       }
//     );

//     // Add SYSTEM section for admin
//     baseMenu.sidenav.push({
//       name: "SYSTEM",
//       items: [
//         {
//           name: "Configuration",
//           items: [
//             { icon: "Users", label: "User Management", route: "/configuration/users", requiredResource: "User Administration" },
//             { icon: "UserCheck", label: "Group Management", route: "/configuration/groups", requiredResource: "Role Management" },
//             { icon: "Shield", label: "Entitlements", route: "/configuration/entitlements", requiredResource: "Role Management" },
//             { icon: "Building", label: "Organizations", route: "/configuration/organizations", requiredResource: "User Administration" },
//             { icon: "List", label: "Code List", route: "/configuration/code-list", requiredResource: "System Settings" },
//             { icon: "Settings", label: "Configurations", route: "/configuration/configurations", requiredResource: "System Settings" },
//             { icon: "List", label: "Study Configuration", route: "/configuration/study", requiredResource: "Case Management" },
//             { icon: "FileText", label: "Report Type", route: "/configuration/report-type", requiredResource: "System Settings" },
//             { icon: "Globe", label: "Country", route: "/configuration/country", requiredResource: "System Settings" }
//           ]
//         }
//       ]
//     });
//   }

//   return baseMenu;
// };

/**
 * Deprecated: Helper function to check if user has permission (inline to avoid circular dependency)
 */
// const checkPermission = (permissions: Permission[], resourceName: string, actionName: string): boolean => {
//   return permissions.some(
//     permission =>
//       permission.resource_name.toLowerCase() === resourceName.toLowerCase() &&
//       permission.action_name.toLowerCase() === actionName.toLowerCase() &&
//       permission.granted === true
//   );
// };

/**
 * Deprecated: Helper function to check if user has permission for a menu item
 * Checks requiredResource field from menu item directly
 */
// const checkMenuItemPermission = (item: MenuItem, permissions: Permission[]): boolean => {
//   // If item has requiredResource, check permission
//   if (item.requiredResource) {
//     const actionName = item.requiredAction || "View";
//     return checkPermission(permissions, item.requiredResource, actionName);
//   }

//   // If no requiredResource defined, allow access (for public items)
//   return true;
// };

/**
 * Deprecated: Filter menu items based on user permissions
 */
// const filterMenuByPermissions = (menu: MenuConfig, permissions: Permission[]): MenuConfig => {
//   const filteredMenu: MenuConfig = {
//     sidenav: []
//   };

//   // Iterate through each section
//   menu.sidenav.forEach(section => {
//     const filteredSection = {
//       name: section.name,
//       items: section.items.map(subsection => {
//         const filteredSubsection = {
//           name: subsection.name,
//           items: subsection.items.filter(item =>
//             checkMenuItemPermission(item, permissions)  // Pass full item object
//           )
//         };
//         return filteredSubsection;
//       }).filter(subsection => subsection.items.length > 0) // Remove empty subsections
//     };

//     // Only add section if it has items
//     if (filteredSection.items.length > 0) {
//       filteredMenu.sidenav.push(filteredSection);
//     }
//   });

//   return filteredMenu;
// };

// Deprecated: Async thunk for fetching user menu configuration (legacy - role-based only)
// export const fetchUserMenuConfig = createAsyncThunk(
//   'layoutConfig/fetchUserMenuConfig',
//   async (userRole: string) => {
//     // Simply generate menu based on the provided role
//     // No API calls to avoid circular dependencies
//     return generateMenuFromRole(userRole);
//   }
// );

// Deprecated: New async thunk for fetching permissions and generating filtered menu
// export const fetchUserPermissionsAndMenu = createAsyncThunk(
//   'layoutConfig/fetchUserPermissionsAndMenu',
//   async (userRole: string, { rejectWithValue }) => {
//     try {
//       // Dynamic import to avoid circular dependency
//       const { getMyPermissions } = await import('@/lib/api/entitlements');

//       // Fetch user permissions from API
//       const permissionsData = await getMyPermissions();
//       const permissions = permissionsData.permissions || [];

//       // Generate base menu from role
//       const baseMenu = generateMenuFromRole(userRole);

//       // Filter menu based on permissions
//       const filteredMenu = filterMenuByPermissions(baseMenu, permissions);

//       return {
//         menu: filteredMenu,
//         permissions: permissions
//       };
//     } catch (error) {
//       console.error('Error fetching permissions:', error);
//       // Fallback to role-based menu if API fails
//       const fallbackMenu = generateMenuFromRole(userRole);
//       return {
//         menu: fallbackMenu,
//         permissions: [],
//         error: 'Failed to fetch permissions, using role-based menu'
//       };
//     }
//   },
//   {
//     condition: (_userRole, { getState }) => {
//       const state: any = getState();
//       const loading = state.layoutConfig?.loading;
//       const loaded = state.layoutConfig?.permissionsLoaded;
//       // Cancel if already loading or already loaded
//       return !loading && !loaded;
//     },
//   }
// );

interface LayoutConfigState {
  layoutConfig: AppLayoutConfig;
  loading: boolean;
  error: string | null;
  userPermissions: Permission[];
  permissionsLoaded: boolean;
  allowedRoutes: string[];
  warning: string | null;
}

const initialState: LayoutConfigState = {
  loading: false,
  error: null,
  userPermissions: [],
  permissionsLoaded: false,
  allowedRoutes: [],
  warning: null,
  layoutConfig: {
    navbar: {
      user: {
        firstName: "",
        lastName: "",
        avatar: "",
        email: "",
      },
      logo: "/theragenx-logo.png",
      notifications: [],
      appName: "",
    },
    sidenav: [],
  },
};

const layoutConfigSlice = createSlice({
  name: "layoutConfig",
  initialState,
  reducers: {
    setLayoutConfig(state, action: PayloadAction<AppLayoutConfig>) {
      state.layoutConfig = action.payload;
    },
    setUserInfo(state, action: PayloadAction<{ firstName: string; lastName: string; email: string; avatar: string }>) {
      state.layoutConfig.navbar.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialState)
      .addCase(loginSuccess, () => initialState)
      // Handle dynamic menu fetch
      .addCase(fetchDynamicMenu.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDynamicMenu.fulfilled, (state, action) => {
        state.loading = false;

        state.layoutConfig.sidenav = action.payload.sidenav;
        state.allowedRoutes = action.payload.allowedRoutes;
        state.permissionsLoaded = true;
        state.warning = action.payload.warning;

      })
      .addCase(fetchDynamicMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch menu';
        state.permissionsLoaded = false;
      });
  },
});

export const { setLayoutConfig, setUserInfo } = layoutConfigSlice.actions;

export const selectAllowedRoutes = (state: { layoutConfig: LayoutConfigState }) => state.layoutConfig.allowedRoutes;
export const selectMenuStatus = (state: { layoutConfig: LayoutConfigState }) => state.layoutConfig;

export default layoutConfigSlice.reducer;
